import instance, { ApiResponse } from 'api';
import Button from 'components/button/Button';
import { Spinner } from 'components/spinner/Spinner';
import { parseISO } from 'date-fns';
import { endpoints } from 'endpoints.config';
import { localizedFormatDate } from 'helpers/language';
import { TFAType } from 'pages/register/models';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ModalTypes, openModal } from 'reducers/modal';
import { debounce } from 'lodash';
import { TfaToggle } from 'components/tfaToggle/tfatoggle';
import { curry } from 'ramda';
import { Toast, ToastMessageReason } from 'helpers/toast';
import { selectCultureCode } from 'reducers/language';
import { useTranslation } from 'react-i18next';
import ErrorBoundary from 'components/page/ErrorBoundary';
import ErrorFallback from 'components/fallbacks/ErrorFallback';

interface UserSecurityData {
    phoneNumber: string | null;
    emailAddress: string;
    bTwoFactorAppAuthEnabled: boolean;
    bTwoFactorSMSAuthEnabled: boolean;
    bTwoFactorAppAuthSaved: boolean;
    bTwoFactorSMSAuthSaved: boolean;
    loginList: {
        browser: string;
        dateTime: string;
        ip: string;
        locationInfo: string;
    }[];
}

export const Security = () => {
    const [securityData, setSecurityData] = useState<UserSecurityData | null>(null);
    const [initialToggles, setInitialToggles] = useState<{
        bTwoFactorAppAuthEnabled: boolean;
        bTwoFactorSMSAuthEnabled: boolean;
    } | null>(null);
    const dispatch = useDispatch();
    const cultureCode = useSelector(selectCultureCode);
    const { t } = useTranslation('security');

    const fetchDetailsFromAPI = useCallback(() => {
        instance
            .get<ApiResponse<UserSecurityData>>(endpoints.profilemodule.usersecurityinfo)
            .then((response) => {
                if (response.data.status === '1') {
                    setSecurityData(response.data.details);
                    const { bTwoFactorAppAuthEnabled, bTwoFactorSMSAuthEnabled } =
                        response.data.details;
                    setInitialToggles({ bTwoFactorSMSAuthEnabled, bTwoFactorAppAuthEnabled });
                }
            });
    }, []);

    const switchAppTFA = useCallback(
        (v: boolean) => {
            if (v === initialToggles?.bTwoFactorAppAuthEnabled) return;
            setInitialToggles((current) =>
                current ? { ...current, bTwoFactorAppAuthEnabled: v } : null
            );
            instance.post(endpoints.profilemodule.flipAppTfa).catch((err) => {
                setInitialToggles((current) =>
                    current ? { ...current, bTwoFactorAppAuthEnabled: !v } : null
                );
                setSecurityData((current) =>
                    current ? { ...current, bTwoFactorAppAuthEnabled: !v } : null
                );
                Toast.openToastMessage(
                    v ? t('errorEnablingAppTfa') : t('errorDisablingAppTfa'),
                    ToastMessageReason.ERROR
                );
            });
        },
        [initialToggles, dispatch]
    );
    const switchSmsTFA = useCallback(
        (v: boolean) => {
            if (v === initialToggles?.bTwoFactorSMSAuthEnabled) return;
            setInitialToggles((current) =>
                current ? { ...current, bTwoFactorSMSAuthEnabled: v } : null
            );
            instance.post(endpoints.profilemodule.flipSmsTfa).catch((err) => {
                setInitialToggles((current) =>
                    current ? { ...current, bTwoFactorSMSAuthEnabled: !v } : null
                );
                setSecurityData((current) =>
                    current ? { ...current, bTwoFactorSMSAuthEnabled: !v } : null
                );
                Toast.openToastMessage(
                    v ? t('errorEnablingSmsTfa') : t('errorDisablingSmsTfa'),
                    ToastMessageReason.ERROR
                );
            });
        },
        [initialToggles, dispatch]
    );

    useEffect(() => {
        fetchDetailsFromAPI();
    }, [fetchDetailsFromAPI]);

    // TODO(HC): Await response from endpoint before rendering the security page.
    return securityData ? (
        <div className="SecurityPage">
            <div className="SecurityPageSection">
                <div className="Content">
                    <h2 className="notranslate" translate="no">{t('security')}</h2>
                    <div className="Item">
                        <p className="Label notranslate" translate="no">{t('changePassword')}</p>
                        <Button
                            priority="primary"
                            variety="full"
                            className="Button"
                            onClick={() =>
                                dispatch(
                                    openModal({ modalType: ModalTypes.CHANGE_PASSWORD, data: null })
                                )
                            }
                        >
                            <span className="notranslate" translate="no">{t('change')}</span>
                        </Button>
                    </div>
                    <div className="Item">
                        <p className="Label notranslate" translate="no">
                            {t('phoneNumber')}{' '}
                            {securityData.phoneNumber ? `(${securityData.phoneNumber})` : t('missing')}
                        </p>
                        <Button
                            priority="primary"
                            variety="full"
                            className="Button"
                            onClick={() =>
                                dispatch(
                                    openModal({
                                        modalType: ModalTypes.CHANGE_PHONE_NUMBER,
                                        data: null,
                                    })
                                )
                            }
                        >
                            <span className="notranslate" translate="no">{securityData.phoneNumber ? t('change') : t('add', { ns: 'common' })}</span>
                        </Button>
                    </div>
                    <div className="Item">
                        <p className="Label notranslate" translate="no">{t('emailAddress')} ({securityData.emailAddress})</p>
                        <Button
                            priority="primary"
                            variety="full"
                            className="Button"
                            onClick={() =>
                                dispatch(
                                    openModal({
                                        modalType: ModalTypes.CHANGE_EMAIL,
                                        data: null,
                                    })
                                )
                            }
                        >
                            <span className="notranslate" translate="no">{t('change')}</span>
                        </Button>
                    </div>
                    <div className="Item">
                        <p className="Label notranslate" translate="no">{t('accountActivation')}</p>
                        <Button
                            priority="secondary"
                            variety="full"
                            className="Button Deactivate"
                            onClick={() =>
                                dispatch(
                                    openModal({
                                        modalType: ModalTypes.DEACTIVATE_ACCOUNT,
                                        data: null,
                                    })
                                )
                            }
                        >
                            <span className="notranslate" translate="no">{t('deactivateAccount')}</span>
                        </Button>
                    </div>
                </div>
            </div>
            <div className="SecurityPageSection">
                <div className="Content">
                    <h2 className="notranslate" translate="no">{t('twoFactorAuth')}</h2>
                    <div className="Item">
                        <p className="Label notranslate" translate="no">{t('authenticatorApp')}</p>
                        <TfaToggle
                            value={securityData.bTwoFactorAppAuthEnabled}
                            setValue={(v: boolean) => {
                                setSecurityData((current) =>
                                    current ? { ...current, bTwoFactorAppAuthEnabled: v } : null
                                );
                            }}
                            setInitialValue={switchAppTFA}
                            disabled={
                                !securityData.bTwoFactorSMSAuthEnabled ||
                                !securityData.bTwoFactorAppAuthSaved
                            }
                        />
                        <Button
                            priority="primary"
                            variety="full"
                            className="Button"
                            onClick={() =>
                                dispatch(
                                    openModal({
                                        modalType: ModalTypes.UPDATE_APP_TFA,
                                        data: null,
                                    })
                                )
                            }
                        >
                            <span className="notranslate" translate="no">{securityData.bTwoFactorAppAuthSaved ? t('change') : t('add', { ns: 'common' })}</span>
                        </Button>
                    </div>
                    <div className="Item">
                        <p className="Label notranslate" translate="no">
                            {t('smsPhoneNumber')} {securityData.phoneNumber || 'N/A'}
                        </p>
                        <TfaToggle
                            value={securityData.bTwoFactorSMSAuthEnabled}
                            setValue={(v: boolean) => {
                                setSecurityData((current) =>
                                    current ? { ...current, bTwoFactorSMSAuthEnabled: v } : null
                                );
                            }}
                            setInitialValue={switchSmsTFA}
                            disabled={
                                !securityData.bTwoFactorAppAuthEnabled ||
                                !securityData.bTwoFactorSMSAuthSaved
                            }
                        />
                        <Button
                            priority="primary"
                            variety="full"
                            className="Button"
                            onClick={() =>
                                dispatch(
                                    openModal({
                                        modalType: ModalTypes.CHANGE_PHONE_NUMBER,
                                        data: null,
                                    })
                                )
                            }
                        >
                            <span className="notranslate" translate="no">{t('change')}</span>
                        </Button>
                    </div>
                </div>
            </div>
            <div className="SecurityPageSection">
                <h2 className="notranslate" translate="no">{t('lastSessions')}</h2>
                <div className="TableContainer">
                    <table className="LoginTable">
                        <thead>
                            <tr>
                                <th className="notranslate" translate="no">{t('signedIn')}</th>
                                <th className="notranslate" translate="no">{t('browser')}</th>
                                <th className="notranslate" translate="no">{t('ipAddress')}</th>
                                <th className="notranslate" translate="no">{t('location')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {securityData.loginList &&
                                securityData.loginList.map((row) => {
                                    return (
                                        <tr>
                                            <td>
                                                {row.dateTime
                                                    ? localizedFormatDate(
                                                          parseISO(row.dateTime),
                                                          cultureCode ?? undefined,
                                                          'Pp'
                                                      )
                                                    : '-'}
                                            </td>
                                            <td>{row.browser ?? '-'}</td>
                                            <td>{row.ip ?? '-'}</td>
                                            <td>{row.locationInfo ?? '-'}</td>
                                        </tr>
                                    );
                                })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    ) : (
        <Spinner />
    );
};
